import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'

import { api, ROLE_LABELS, type ListResponse, type Me, type Member } from '../lib/api'
import { listItemVariants, listVariants, pageVariants, springSoft } from '../lib/motion'
import { Banner, Card, Chip } from '../ui'

export function HomePage({ me }: { me: Me }) {
  const tenant = me.active_tenant

  const members = useQuery({
    queryKey: ['members', tenant?.id],
    queryFn: () => api.get<ListResponse<Member>>('/api/members'),
    enabled: Boolean(tenant),
  })

  if (!tenant) {
    return (
      <Card title="Noch kein Betrieb">
        <div className="stack">
          <p>
            Dein Konto gehört bisher zu keinem Betrieb. Sobald dich jemand einlädt, erscheint er
            hier.
          </p>
          {me.is_superadmin && (
            <p>
              Als Superadmin kannst du in der <Link to="/verwaltung">Verwaltung</Link> einen
              Betrieb anlegen.
            </p>
          )}
        </div>
      </Card>
    )
  }

  const items = members.data?.items ?? []
  const byRole = (role: string) => items.filter((member) => member.role === role).length
  // Nicht „noch nicht bestätigt": Das zählte jede angelegte Aushilfe mit und
  // mahnte etwas an, das gar nicht offen ist. Wer kein Konto hat, wartet auf
  // keine Bestätigung.
  const withoutAccount = items.filter((member) => !member.has_account).length

  return (
    <motion.div
      className="stack"
      variants={pageVariants}
      initial="hidden"
      animate="visible"
      transition={springSoft()}
    >
      <div>
        <h1>{tenant.name}</h1>
        <p className="muted">
          Du bist hier {ROLE_LABELS[tenant.role]}.{' '}
          {tenant.role === 'employee'
            ? 'Deine Schichten erscheinen hier, sobald geplant wurde.'
            : 'Die Schichtplanung kommt in der nächsten Ausbaustufe dazu.'}
        </p>
      </div>

      <motion.div
        className="grid-2"
        variants={listVariants(4)}
        initial="hidden"
        animate="visible"
      >
        {[
          { label: 'Im Team', value: items.length },
          { label: 'Schichtleitung', value: byRole('manager') + byRole('owner') },
          { label: 'Mitarbeitende', value: byRole('employee') },
          // „Ohne Zugang" ist eine Angabe, keine Mahnung — für viele Betriebe
          // ist das der Dauerzustand eines Teils der Belegschaft. Offene
          // Bestätigungen stehen dafür auf der Team-Seite an der Person; ein
          // Kennwert, dessen Bedeutung je nach Lage umspringt, wäre schlimmer
          // als keiner.
          { label: 'Ohne Zugang', value: withoutAccount },
        ].map((stat) => (
          <motion.div key={stat.label} variants={listItemVariants} transition={springSoft()}>
            <Card>
              <div className="stat">
                <span className="stat__value">{members.isPending ? '–' : stat.value}</span>
                <span className="stat__label">{stat.label}</span>
              </div>
            </Card>
          </motion.div>
        ))}
      </motion.div>

      <Card
        title="Team"
        action={
          <Link to="/team" className="btn btn--text btn--small">
            Alle ansehen
          </Link>
        }
      >
        {members.isError && <Banner tone="error">Die Liste konnte nicht geladen werden.</Banner>}
        <ul className="stack stack--tight" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {items.slice(0, 5).map((member) => (
            <li key={member.membership_id} className="row row--between">
              <span>
                {member.full_name}
                {member.user_id === me.id && <span className="muted"> · du</span>}
              </span>
              <Chip tone={member.role === 'owner' ? 'primary' : 'neutral'}>
                {ROLE_LABELS[member.role]}
              </Chip>
            </li>
          ))}
        </ul>
      </Card>

      <Banner tone="info">
        <div>
          <strong>Stand der Entwicklung.</strong> Phase 1 ist fertig: Konten, Betriebe, Rollen,
          Einladungen und das Protokoll. Als Nächstes kommen Standorte, Positionen und das
          Planungsraster.
        </div>
      </Banner>
    </motion.div>
  )
}
